// Cờ non-interactive của cụm `avatar add repo` / `remove repo` (T3).
//
// ⚠️ CHÊNH LỆCH CÓ CHỦ Ý SO VỚI BẢNG CỜ Ở phase-04 — đo lại source 2026-08-19:
//   · **KHÔNG khai `--branch`**: không site prompt nào hỏi branch (`add-repo.ts`
//     và `clone-repo-with-recovery-menu.ts` đều clone nhánh mặc định). Khai cờ cho
//     câu hỏi không tồn tại thì ca âm của nó XANH GIẢ vĩnh viễn.
//   · **KHÔNG khai `--remote-host`**: nhánh tạo remote chỉ có GitHub
//     (`createEmptyRepoWithRecovery` / `createGithubRemoteFromFolder`), không có
//     điểm hỏi nào chọn host.
//   Theo D9, site không có cờ khả thi KHÔNG vào mẫu số — ở đây là chiều ngược lại:
//   cờ không có site thì không được vào mẫu số.
//
// ⚠️ `--yes` TUYỆT ĐỐI không chạm confirm xoá folder của `remove repo`: đó là xác
// nhận mất dữ liệu (`default: false`). Non-interactive phải nói thẳng ý định bằng
// `--keep-files` hoặc `--delete-files`; không cờ nào ⇒ thoát ≠0, KHÔNG xoá gì.

import type { Command } from "commander";
import { MissingFlagError, requireFlag } from "./non-interactive-context.js";

export const REPO_SOURCES = ["url", "folder", "new"] as const;
export const CLONE_FAIL_ACTIONS = ["abort", "retry", "skip"] as const;
export const EXISTING_DIR_ACTIONS = ["abort", "use", "backup"] as const;
export const REMOTE_CREATE_FAIL_ACTIONS = ["abort", "rename", "skip"] as const;
export const VISIBILITIES = ["private", "public"] as const;
export const DIRTY_FOLDER_ACTIONS = ["abort", "proceed"] as const;

export type RepoSource = (typeof REPO_SOURCES)[number];
export type CloneFailAction = (typeof CLONE_FAIL_ACTIONS)[number];
export type ExistingDirAction = (typeof EXISTING_DIR_ACTIONS)[number];
export type RemoteCreateFailAction = (typeof REMOTE_CREATE_FAIL_ACTIONS)[number];
export type Visibility = (typeof VISIBILITIES)[number];

/** Giá trị cờ của `avatar add repo`, tên camelCase đúng như commander sinh ra. */
export interface AddRepoFlags {
  url?: string;
  name?: string;
  yes?: boolean;
  repoSource?: string;
  folderPath?: string;
  newRepoName?: string;
  remoteVisibility?: string;
  createRemote?: string;
  onCloneFail?: string;
  onExistingDir?: string;
  onRemoteCreateFail?: string;
  onDirtyFolder?: string;
  // 8 cờ nhóm A — T3 đăng ký hộ, T4 tiêu thụ (luật "chủ file khai option").
  patUser?: string;
  patStdin?: boolean;
  onRemoteAccessFail?: string;
  remoteUrl?: string;
  onFailure?: string;
  resetGit?: string;
  newRemoteVisibility?: string;
  gitnexusWiki?: string;
}

export interface RemoveRepoFlags {
  keepFiles?: boolean;
  deleteFiles?: boolean;
}

const ADD = "add repo";

/** Ép giá trị cờ nằm trong tập cho phép. Vắng mặt → `fallback`. */
export function requireOneOf<T extends string>(
  raw: string | undefined,
  allowed: readonly T[],
  flag: string,
  fallback?: T,
): T {
  if (raw === undefined || raw === "") {
    if (fallback !== undefined) return fallback;
    throw new MissingFlagError({
      missingFlag: flag,
      command: ADD,
      hint: `${flag} ${allowed.join("|")}`,
    });
  }
  if (!(allowed as readonly string[]).includes(raw)) {
    throw new Error(`${flag} "${raw}" không hợp lệ. Chọn: ${allowed.join(", ")}`);
  }
  return raw as T;
}

/** Đăng ký cờ cho `avatar add repo` — gồm cả 8 cờ nhóm A mà T4 sẽ đọc. */
export function registerAddRepoFlags(cmd: Command): Command {
  return (
    cmd
      .option(`--repo-source <${REPO_SOURCES.join("|")}>`, "Nguồn repo, thay câu hỏi đầu flow")
      .option("--folder-path <path>", "Đường dẫn folder có sẵn (--repo-source folder)")
      .option("--new-repo-name <name>", "Tên repo GitHub mới (--repo-source new)")
      .option(`--remote-visibility <${VISIBILITIES.join("|")}>`, "Visibility repo tạo mới")
      .option("--create-remote <yes|no>", "Folder chưa có remote: có tạo remote GitHub không")
      .option(`--on-clone-fail <${CLONE_FAIL_ACTIONS.join("|")}>`, "Xử lý khi clone thất bại")
      .option(
        `--on-existing-dir <${EXISTING_DIR_ACTIONS.join("|")}>`,
        "Xử lý khi src/<name> đã tồn tại",
      )
      .option(
        `--on-remote-create-fail <${REMOTE_CREATE_FAIL_ACTIONS.join("|")}>`,
        "Xử lý khi tạo remote thất bại",
      )
      .option(`--on-dirty-folder <${DIRTY_FOLDER_ACTIONS.join("|")}>`, "Xử lý khi folder nguồn bẩn")
      // ↓ 8 cờ nhóm A: T3 đăng ký, T4 tiêu thụ. Xem phase-05 cho ngữ nghĩa.
      .option("--pat-user <username>", "Username đi kèm PAT cho host không phải GitHub")
      .option("--pat-stdin", "Đọc PAT từ stdin (không qua argv)")
      .option(
        "--on-remote-access-fail <abort|switch-account|use-pat>",
        "Xử lý khi không truy cập được remote",
      )
      .option("--remote-url <url>", "URL remote thay thế khi cần đổi")
      .option("--on-failure <abort|retry|skip>", "Xử lý mặc định cho menu recovery chung")
      .option("--reset-git <yes|no>", "Có reset lịch sử git của folder không")
      .option(
        `--new-remote-visibility <${VISIBILITIES.join("|")}>`,
        "Visibility remote tạo mới (T4)",
      )
      .option("--gitnexus-wiki <yes|no>", "Có sinh wiki GitNexus sau khi add repo không")
  );
}

/** Đăng ký cờ cho `avatar remove repo`. KHÔNG có `--yes` ở đây — cố ý. */
export function registerRemoveRepoFlags(cmd: Command): Command {
  return cmd.option("--delete-files", "Xoá luôn folder src/<name> (ý định xoá tường minh)");
}

/**
 * Quyết định xoá folder cho `remove repo` ở chế độ non-interactive.
 *
 * Ba trạng thái, KHÔNG có mặc định im lặng: giữ · xoá · chưa nói gì (lỗi).
 * Gọi hàm này TRƯỚC `removeRepoFromManifest` — manifest bị gỡ ở bước trước câu
 * hỏi xoá folder, nên kiểm muộn để lại workspace nửa vời: mất trong `repos.json`
 * mà folder vẫn còn.
 */
export function resolveDeleteFolderDecision(flags: RemoveRepoFlags): boolean {
  if (flags.keepFiles === true && flags.deleteFiles === true) {
    throw new Error("--keep-files và --delete-files loại trừ nhau — chọn đúng một cái.");
  }
  if (flags.keepFiles === true) return false;
  if (flags.deleteFiles === true) return true;
  throw new MissingFlagError({
    missingFlag: "--keep-files|--delete-files",
    command: "remove repo",
    hint: "--keep-files (giữ folder) hoặc --delete-files (xoá folder) — không có mặc định",
  });
}

/** Nguồn repo ở chế độ non-interactive. `--url` có mặt ⇒ suy ra `url`, khỏi bắt khai lại. */
export function resolveRepoSourceFlag(flags: AddRepoFlags): RepoSource {
  if (flags.repoSource === undefined || flags.repoSource === "") {
    if (flags.url) return "url";
    throw new MissingFlagError({
      missingFlag: "--url",
      command: ADD,
      hint: `--url <git-url> (hoặc --repo-source ${REPO_SOURCES.join("|")})`,
    });
  }
  return requireOneOf(flags.repoSource, REPO_SOURCES, "--repo-source");
}

/** Giá trị bắt buộc theo từng nguồn. Ném ở cờ đầu tiên còn thiếu. */
export function assertAddRepoFlagsComplete(flags: AddRepoFlags): RepoSource {
  // Kiểm GIÁ TRỊ của mọi cờ enum trước, để typo được báo ngay không phụ thuộc nhánh.
  requireOneOf(flags.onCloneFail, CLONE_FAIL_ACTIONS, "--on-clone-fail", "abort");
  requireOneOf(flags.onExistingDir, EXISTING_DIR_ACTIONS, "--on-existing-dir", "abort");
  requireOneOf(
    flags.onRemoteCreateFail,
    REMOTE_CREATE_FAIL_ACTIONS,
    "--on-remote-create-fail",
    "abort",
  );
  requireOneOf(flags.onDirtyFolder, DIRTY_FOLDER_ACTIONS, "--on-dirty-folder", "abort");

  const source = resolveRepoSourceFlag(flags);

  if (source === "url") {
    requireFlag(flags.url, {
      flag: "--url",
      command: ADD,
      hint: "--url git@github.com:org/repo.git",
    });
  }
  if (source === "folder") {
    requireFlag(flags.folderPath, {
      flag: "--folder-path",
      command: ADD,
      hint: "--folder-path /duong/dan/toi/folder",
    });
  }
  if (source === "new") {
    requireFlag(flags.newRepoName, {
      flag: "--new-repo-name",
      command: ADD,
      hint: "--new-repo-name ten-repo-moi",
    });
    requireOneOf(flags.remoteVisibility, VISIBILITIES, "--remote-visibility");
  }
  return source;
}
